/**
 * Frontend Telemetry Service
 *
 * Provides distributed tracing and error reporting for the frontend.
 * Integrates with OpenTelemetry for trace context propagation.
 */

import type { Span} from "@opentelemetry/api";
import { trace, context, SpanKind, SpanStatusCode, propagation } from "@opentelemetry/api";
import { WebTracerProvider, SimpleSpanProcessor, ConsoleSpanExporter } from "@opentelemetry/sdk-trace-web";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from "@opentelemetry/semantic-conventions";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { W3CTraceContextPropagator } from "@opentelemetry/core";

export interface TraceContext {
    traceId: string;
    spanId: string;
    traceparent: string;
}

export interface FrontendErrorReport {
    errorMessage: string;
    errorStack?: string;
    errorName?: string;
    componentStack?: string;
    url: string;
    userAgent: string;
    timestamp: string;
    traceId?: string;
    spanId?: string;
    metadata?: Record<string, unknown>;
}

export interface TelemetryEventOptions {
    name: string;
    properties?: Record<string, string>;
    measurements?: Record<string, number>;
}

class TelemetryService {
    private provider: WebTracerProvider | null = null;
    private serviceName = "webapp-frontend";
    private serviceVersion = "1.0.0";
    private isInitialized = false;
    private currentTraceContext: TraceContext | null = null;

    /**
     * Initialize the telemetry service
     * Should be called early in the app lifecycle
     */
    initialize(): void {
        if (this.isInitialized) return;

        try {
            // Set up W3C trace context propagation
            propagation.setGlobalPropagator(new W3CTraceContextPropagator());

            // Create resource with service info
            const resource = resourceFromAttributes({
                [ATTR_SERVICE_NAME]: this.serviceName,
                [ATTR_SERVICE_VERSION]: this.serviceVersion
            });

            // Create trace provider (console exporter for development debugging)
            this.provider = new WebTracerProvider({
                resource,
                spanProcessors:
                    import.meta.env.DEV
                        ? [new SimpleSpanProcessor(new ConsoleSpanExporter())]
                        : []
            });

            // Register provider with Zone context manager for async context propagation
            this.provider.register({
                contextManager: new ZoneContextManager()
            });

            this.isInitialized = true;
            console.warn("[Telemetry] Frontend telemetry initialized");
        } catch (error) {
            console.error("[Telemetry] Failed to initialize:", error);
        }
    }

    /**
     * Get a tracer instance
     */
    getTracer(name?: string) {
        return trace.getTracer(name || this.serviceName, this.serviceVersion);
    }

    /**
     * Generate a new trace context
     * Used to start a trace for a user action
     */
    generateTraceContext(): TraceContext {
        const tracer = this.getTracer();
        const span = tracer.startSpan("user-action", { kind: SpanKind.CLIENT });
        const spanContext = span.spanContext();

        const traceContext: TraceContext = {
            traceId: spanContext.traceId,
            spanId: spanContext.spanId,
            traceparent: `00-${spanContext.traceId}-${spanContext.spanId}-0${spanContext.traceFlags}`
        };

        // Store current context
        this.currentTraceContext = traceContext;

        // End the span (we just needed the IDs)
        span.end();

        return traceContext;
    }

    /**
     * Get the current trace context
     */
    getCurrentTraceContext(): TraceContext | null {
        // Try to get from active span first
        const activeSpan = trace.getActiveSpan();
        if (activeSpan) {
            const spanContext = activeSpan.spanContext();
            return {
                traceId: spanContext.traceId,
                spanId: spanContext.spanId,
                traceparent: `00-${spanContext.traceId}-${spanContext.spanId}-0${spanContext.traceFlags}`
            };
        }
        return this.currentTraceContext;
    }

    /**
     * Create headers with trace context for fetch calls
     */
    getTraceHeaders(): Record<string, string> {
        const ctx = this.getCurrentTraceContext();
        if (ctx) {
            return {
                traceparent: ctx.traceparent
            };
        }
        return {};
    }

    /**
     * Start a new span for tracing an operation
     */
    startSpan(name: string, kind: SpanKind = SpanKind.INTERNAL): Span {
        const tracer = this.getTracer();
        return tracer.startSpan(name, { kind });
    }

    /**
     * Execute a function within a span context
     */
    async withSpan<T>(name: string, fn: (span: Span) => Promise<T>): Promise<T> {
        const span = this.startSpan(name);

        try {
            const result = await context.with(trace.setSpan(context.active(), span), () => fn(span));
            span.setStatus({ code: SpanStatusCode.OK });
            return result;
        } catch (error) {
            span.setStatus({
                code: SpanStatusCode.ERROR,
                message: error instanceof Error ? error.message : String(error)
            });
            if (error instanceof Error) {
                span.recordException(error);
            }
            throw error;
        } finally {
            span.end();
        }
    }

    /**
     * Report an error to the backend
     */
    async reportError(error: Error, componentStack?: string, metadata?: Record<string, unknown>): Promise<void> {
        const traceCtx = this.getCurrentTraceContext();

        const errorReport: FrontendErrorReport = {
            errorMessage: error.message,
            errorStack: error.stack,
            errorName: error.name,
            componentStack,
            url: window.location.href,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            traceId: traceCtx?.traceId,
            spanId: traceCtx?.spanId,
            metadata
        };

        try {
            const response = await fetch("/api/telemetry/error", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...this.getTraceHeaders()
                },
                credentials: "include",
                body: JSON.stringify(errorReport)
            });

            if (!response.ok) {
                console.error("[Telemetry] Failed to report error:", response.statusText);
            }
        } catch (reportError) {
            // Don't throw - we don't want error reporting to cause more errors
            console.error("[Telemetry] Error reporting failed:", reportError);
        }
    }

    /**
     * Track a custom event
     */
    async trackEvent(options: TelemetryEventOptions): Promise<void> {
        const traceCtx = this.getCurrentTraceContext();

        try {
            await fetch("/api/telemetry/event", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...this.getTraceHeaders()
                },
                credentials: "include",
                body: JSON.stringify({
                    name: options.name,
                    properties: options.properties,
                    measurements: options.measurements,
                    traceId: traceCtx?.traceId,
                    spanId: traceCtx?.spanId
                })
            });
        } catch (error) {
            console.error("[Telemetry] Failed to track event:", error);
        }
    }

    /**
     * Create a wrapped fetch function that includes trace context
     * Use this for API calls that need to be traced
     */
    tracedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        // Generate trace context if not already present
        if (!this.currentTraceContext) {
            this.generateTraceContext();
        }

        const headers = new Headers(init?.headers);

        // Add trace headers
        const traceHeaders = this.getTraceHeaders();
        Object.entries(traceHeaders).forEach(([key, value]) => {
            headers.set(key, value);
        });

        return fetch(input, {
            ...init,
            headers,
            credentials: "include"
        });
    }
}

// Export singleton instance
export const telemetry = new TelemetryService();

// Initialize on import
telemetry.initialize();
